import { MonitorDown, Trash2, UploadCloud } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Input, Skeleton, Badge } from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { formatDateTime } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';

type ReleaseKind = 'terminal' | 'android' | 'mandob' | 'manager';

/** The kinds whose updater verifies a minisign signature — mirrors SIGNED_KINDS on the API. */
const SIGNED_KINDS: readonly ReleaseKind[] = ['terminal', 'manager'];

interface TerminalRelease {
  id: string;
  kind: ReleaseKind;
  version: string;
  notes: string | null;
  fileName: string;
  fileSize: number;
  createdAt: string;
}

/** Newest release by semver PER KIND — the one each app's consumers are served. */
function latestVersion(rows: TerminalRelease[], kind: ReleaseKind): string | null {
  const parse = (v: string): number[] => v.split('-')[0]!.split('.').map(Number);
  let top: string | null = null;
  for (const r of rows) {
    if (r.kind !== kind) continue;
    if (!top) { top = r.version; continue; }
    const a = parse(r.version); const b = parse(top);
    for (let i = 0; i < 3; i++) {
      if ((a[i] ?? 0) > (b[i] ?? 0)) { top = r.version; break; }
      if ((a[i] ?? 0) < (b[i] ?? 0)) break;
    }
  }
  return top;
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
}

/**
 * Platform-admin releases for the two installable apps: the offline POS desktop
 * terminal (a signed NSIS setup .exe + its Tauri .sig, which every installed
 * terminal self-updates to via the public feed) and the customer Android .apk
 * that tenants hand out from Settings → Downloads. Uploads are stored
 * server-side in the platform DB.
 */
export function PlatformTerminalPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const [rows, setRows] = useState<TerminalRelease[] | null>(null);
  const [kind, setKind] = useState<ReleaseKind>('terminal');
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const exeRef = useRef<HTMLInputElement>(null);
  const sigRef = useRef<HTMLInputElement>(null);
  // Only the Tauri updater verifies a minisign signature; an APK is signed in
  // its own APK-signing block, so there is no .sig to ask for.
  const needsSignature = SIGNED_KINDS.includes(kind);
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    try {
      const r = await platformApi().get<{ items: TerminalRelease[] }>('/terminal-releases');
      setRows(r.data.items);
    } catch {
      toast.error(t('terminal.loadError', { defaultValue: 'Could not load releases.' }));
    }
  };
  useEffect(() => { void load(); }, []);

  const upload = async (): Promise<void> => {
    const exe = exeRef.current?.files?.[0];
    const sig = sigRef.current?.files?.[0];
    if (!version.trim() || !exe || (needsSignature && !sig)) {
      toast.error(needsSignature
        ? t('terminal.missingFields', { defaultValue: 'Version, the installer (.exe) and its signature (.sig) are all required.' })
        : t('terminal.missingFieldsApk', { defaultValue: 'Version and the app file (.apk) are required.' }));
      return;
    }
    setUploading(true);
    try {
      const signature = sig ? (await sig.text()).trim() : '';
      const qs = new URLSearchParams({ kind, version: version.trim(), signature, fileName: exe.name });
      if (notes.trim()) qs.set('notes', notes.trim());
      const body = await exe.arrayBuffer();
      await platformApi().post(`/terminal-releases?${qs.toString()}`, body, {
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      toast.success(needsSignature
        ? t('terminal.uploaded', { defaultValue: 'Version {{v}} published — terminals will pick it up on their next update check.', v: version.trim() })
        : t('terminal.uploadedApk', { defaultValue: 'Version {{v}} published — tenants can download it from Settings → Downloads.', v: version.trim() }));
      setVersion(''); setNotes('');
      if (exeRef.current) exeRef.current.value = '';
      if (sigRef.current) sigRef.current.value = '';
      await load();
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      toast.error(code === 'version_exists'
        ? t('terminal.versionExists', { defaultValue: 'That version already exists — bump the version number.' })
        : t('terminal.uploadError', { defaultValue: 'Upload failed.' }));
    } finally {
      setUploading(false);
    }
  };

  /**
   * Fetch the artifact through the admin, BY-ID route.
   *
   * This table lists every kind together, and the old plain <a href> guessed the
   * path from the kind: anything that wasn't android went to the terminal-only
   * updater feed, so downloading a rep's APK or the manager installer handed back
   * the till's .exe — or a 404 when no terminal shared that version number. An id
   * names exactly one row, so it cannot resolve to the wrong app. Bearer-gated,
   * hence the blob dance rather than a link.
   */
  const download = async (r: TerminalRelease): Promise<void> => {
    setDownloading(r.id);
    try {
      const res = await platformApi().get<Blob>(`/terminal-releases/${r.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('terminal.downloadError', { defaultValue: 'The download failed.' }));
    } finally {
      setDownloading(null);
    }
  };

  const remove = async (r: TerminalRelease): Promise<void> => {
    if (!window.confirm(t('terminal.confirmDelete', { defaultValue: 'Delete version {{v}}?', v: r.version }))) return;
    try {
      await platformApi().delete(`/terminal-releases/${r.id}`);
      toast.success(t('terminal.deleted', { defaultValue: 'Version {{v}} deleted.', v: r.version }));
      await load();
    } catch {
      toast.error(t('terminal.deleteError', { defaultValue: 'Could not delete the release.' }));
    }
  };

  // Per kind, so every app's newest row is badged — not just the two that
  // existed when this page was written.
  const latestByKind: Record<ReleaseKind, string | null> = {
    terminal: rows ? latestVersion(rows, 'terminal') : null,
    android: rows ? latestVersion(rows, 'android') : null,
    mandob: rows ? latestVersion(rows, 'mandob') : null,
    manager: rows ? latestVersion(rows, 'manager') : null,
  };
  const kindLabel = (k: ReleaseKind): string => ({
    terminal: t('terminal.kindTerminalShort', { defaultValue: 'Terminal' }),
    android: t('terminal.kindAndroidShort', { defaultValue: 'Android' }),
    mandob: t('terminal.kindMandobShort', { defaultValue: 'Mandob' }),
    manager: t('terminal.kindManagerShort', { defaultValue: 'Manager' }),
  })[k];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-info/15 text-info">
          <MonitorDown className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('terminal.title', { defaultValue: 'Sales Terminal app' })}</h1>
          <p className="text-sm text-fg-muted">
            {t('terminal.subtitle', { defaultValue: 'Publish signed installer updates — every installed POS terminal self-updates to the newest version here.' })}
          </p>
        </div>
      </header>

      <Card className="glass-panel p-4">
        <h2 className="mb-3 text-sm font-semibold">{t('terminal.publish', { defaultValue: 'Publish a new version' })}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-fg-muted">{t('terminal.kind', { defaultValue: 'App' })}</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ReleaseKind)}
              className="h-10 rounded-md border border-border bg-bg-subtle px-3 text-sm"
            >
              <option value="terminal">{t('terminal.kindTerminal', { defaultValue: 'Cashier terminal (Windows .exe)' })}</option>
              <option value="manager">{t('terminal.kindManager', { defaultValue: 'Manager app (Windows .exe)' })}</option>
              <option value="android">{t('terminal.kindAndroid', { defaultValue: 'Customer app (Android .apk)' })}</option>
              <option value="mandob">{t('terminal.kindMandob', { defaultValue: 'Field rep app (Android .apk)' })}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-fg-muted">{t('terminal.version', { defaultValue: 'Version (e.g. 0.2.1)' })}</span>
            <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="0.2.1" className="font-mono" />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-fg-muted">{t('terminal.notes', { defaultValue: 'Release notes (shown to the cashier)' })}</span>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('terminal.notesPh', { defaultValue: 'Bug fixes and improvements' })} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-fg-muted">
              {needsSignature
                ? t('terminal.exe', { defaultValue: 'Installer (…-setup.exe)' })
                : t('terminal.apk', { defaultValue: 'App file (….apk)' })}
            </span>
            <input ref={exeRef} type="file" accept={needsSignature ? '.exe' : '.apk'} className="rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm file:me-3 file:rounded file:border-0 file:bg-bg-muted file:px-2 file:py-1" />
          </label>
          {/* Rendered only for the terminal: the updater is the only consumer
              that verifies a minisign signature. */}
          {needsSignature && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-fg-muted">{t('terminal.sig', { defaultValue: 'Signature file (…-setup.exe.sig)' })}</span>
              <input ref={sigRef} type="file" accept=".sig" className="rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm file:me-3 file:rounded file:border-0 file:bg-bg-muted file:px-2 file:py-1" />
            </label>
          )}
        </div>
        <div className="mt-3">
          <Button onClick={() => void upload()} disabled={uploading}>
            <UploadCloud className="me-2 h-4 w-4" aria-hidden />
            {uploading ? t('terminal.uploading', { defaultValue: 'Uploading…' }) : t('terminal.uploadBtn', { defaultValue: 'Publish update' })}
          </Button>
        </div>
      </Card>

      <Card className="glass-panel overflow-hidden p-0">
        {rows === null ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-fg-muted">{t('terminal.empty', { defaultValue: 'No releases yet — publish the first installer above.' })}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-start text-xs uppercase text-fg-muted">
                <th className="p-3 text-start">{t('terminal.colKind', { defaultValue: 'App' })}</th>
                <th className="p-3 text-start">{t('terminal.colVersion', { defaultValue: 'Version' })}</th>
                <th className="p-3 text-start">{t('terminal.colNotes', { defaultValue: 'Notes' })}</th>
                <th className="p-3 text-start">{t('terminal.colFile', { defaultValue: 'File' })}</th>
                <th className="p-3 text-start">{t('terminal.colDate', { defaultValue: 'Published' })}</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-0">
                  <td className="p-3 text-fg-muted">{kindLabel(r.kind)}</td>
                  <td className="p-3 font-mono">
                    {r.version}
                    {r.version === latestByKind[r.kind] && (
                      <Badge variant="success" className="ms-2">{t('terminal.latest', { defaultValue: 'Latest' })}</Badge>
                    )}
                  </td>
                  <td className="max-w-[280px] truncate p-3 text-fg-muted">{r.notes ?? '—'}</td>
                  <td className="p-3 text-fg-muted">
                    {/* By id, not by kind+version — see `download`. */}
                    <button
                      type="button"
                      className="text-brand hover:underline disabled:opacity-60"
                      onClick={() => void download(r)}
                      disabled={downloading === r.id}
                    >
                      {r.fileName}
                    </button>{' '}
                    <span className="text-xs">({fmtSize(r.fileSize)})</span>
                  </td>
                  <td className="p-3 text-fg-muted">{formatDateTime(r.createdAt, { locale: i18n.language })}</td>
                  <td className="p-3 text-end">
                    <Button variant="ghost" size="sm" onClick={() => void remove(r)} aria-label={t('terminal.delete', { defaultValue: 'Delete' })}>
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
